import {
  AdminSetAccessWindowDto,
  AdminUpdateUserFeatureFlagsDto,
  AdminUserDetailDto,
  AdminUserSelectDto,
  AdminUsersResponseDto,
  TagSelectDto,
  TagsSelectDto,
  UserProfileRole,
} from '@@types/api.types';
import { useAuth0 } from '@auth0/auth0-react';
import type { FiltersState } from '@components/data-table-filter/core/types';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryKeys } from '@queries/keys.ts';
import { keepPreviousData, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useGetCurrentUser } from './users.query';

type Nullable<T> = T | null | undefined;

export type AdminUsersQueryParams = {
  filters?: Nullable<FiltersState>;
  limit?: Nullable<number>;
  page?: Nullable<number>;
  search?: Nullable<string>;
};

const buildQueryParams = (params: AdminUsersQueryParams) => {
  const queryParams: Record<string, unknown> = {};

  if (params.search) {
    queryParams.search = params.search;
  }

  if (params.filters && params.filters.length > 0) {
    queryParams.filters = JSON.stringify(params.filters);
  }

  if (params.page) {
    queryParams.page = params.page;
  }

  if (params.limit) {
    queryParams.limit = params.limit;
  }

  return queryParams;
};

export const useAdminUsersQuery = (params: AdminUsersQueryParams) => {
  const { apiGet } = useApi();
  const queryParams = buildQueryParams(params);

  return useQuery<AdminUsersResponseDto, ApiErrorWrapper>({
    queryFn: async () => {
      return await apiGet<AdminUsersResponseDto>({
        path: '/v1/admin/users',
        query: queryParams,
      });
    },
    queryKey: queryKeys.adminUsers.getUsers(queryParams),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  });
};

const useUserMutation = (pathBuilder: (id: string) => string) => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminUserSelectDto, Error, string>({
    mutationFn: async (id) => {
      return await apiPost<AdminUserSelectDto>({ path: pathBuilder(id) });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.root });
    },
  });
};

export const useApproveUser = () => useUserMutation((id) => `/v1/admin/users/${id}/approve`);

export const useUnapproveUser = () => useUserMutation((id) => `/v1/admin/users/${id}/unapprove`);

export const useBlockUser = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminUserSelectDto, Error, { id: string; reasonBlocked?: string }>({
    mutationFn: async ({ id, reasonBlocked }) => {
      return await apiPost<AdminUserSelectDto>({
        path: `/v1/admin/users/${id}/block`,
        body: reasonBlocked ? { reasonBlocked } : {},
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.root });
    },
  });
};

export const useUnblockUser = () => useUserMutation((id) => `/v1/admin/users/${id}/unblock`);

export const useResendVerificationEmail = () => useUserMutation((id) => `/v1/admin/users/${id}/resend-verification`);

export const useAdminUserDetailQuery = (userId: string) => {
  const { apiGet } = useApi();

  return useSuspenseQuery<AdminUserDetailDto, ApiErrorWrapper>({
    queryFn: async () => {
      return await apiGet<AdminUserDetailDto>({
        path: `/v1/admin/users/${userId}`,
      });
    },
    queryKey: queryKeys.adminUsers.getUserById(userId),
  });
};

export const useMarkUserAsVerified = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminUserDetailDto, Error, string>({
    mutationFn: async (id) => {
      return await apiPost<AdminUserDetailDto>({ path: `/v1/admin/users/${id}/mark-verified` });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.root });
    },
  });
};

export const useSetAccessWindow = () => {
  const { apiPost } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminUserDetailDto, Error, { id: string; data: AdminSetAccessWindowDto }>({
    mutationFn: async ({ id, data }) => {
      return await apiPost<AdminUserDetailDto>({
        path: `/v1/admin/users/${id}/set-access-window`,
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.root });
    },
  });
};

type AdminTagsQueryParams = {
  onlyUsedByCompanies?: boolean;
};

export const useAdminGetTags = (params?: AdminTagsQueryParams) => {
  const { apiGet } = useApi();
  const { isAuthenticated } = useAuth0();
  const { data: user } = useGetCurrentUser();
  const isAdmin = user?.profile?.role === UserProfileRole.ADMIN;

  const enabled = Boolean(isAuthenticated) && isAdmin;

  return useQuery<TagSelectDto[], ApiErrorWrapper>({
    enabled,
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (params?.onlyUsedByCompanies !== undefined) {
        queryParams.set('onlyUsedByCompanies', String(params.onlyUsedByCompanies));
      }

      const queryString = queryParams.toString();
      const path = queryString ? `/v1/admin/tags?${queryString}` : '/v1/admin/tags';

      const result = await apiGet<TagsSelectDto>({ path });
      return result.tags;
    },
    queryKey: enabled ? queryKeys.tags.getTags(params) : queryKeys.placeholders.default,
  });
};

export const useAdminUpdateUserFeatureFlags = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<AdminUserDetailDto, Error, { id: string; data: AdminUpdateUserFeatureFlagsDto }>({
    mutationFn: async ({ id, data }) => {
      return await apiPatch<AdminUserDetailDto>({
        path: `/v1/admin/users/${id}/feature-flags`,
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.root });
    },
  });
};

export const useAdminUpdateUserAccessControls = () => {
  const { apiPatch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    AdminUserDetailDto,
    ApiErrorWrapper,
    {
      id: string;
      organizationBlacklist?: string[];
      organizationWhitelist?: string[];
      tags: string[];
    }
  >({
    mutationFn: async ({ id, organizationBlacklist, organizationWhitelist, tags }) => {
      return await apiPatch<AdminUserDetailDto>({
        body: { organizationBlacklist, organizationWhitelist, tags },
        path: `/v1/admin/users/${id}/access-controls`,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.root });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers.getUserById(variables.id) });
      // Invalidate tags query in case new tags were created
      queryClient.invalidateQueries({ queryKey: queryKeys.tags.root });
    },
  });
};

export const useAdminImpersonateUser = () => {
  const { apiPost } = useApi();

  return useMutation<void, ApiErrorWrapper, string>({
    mutationFn: async (userId) => {
      await apiPost({
        path: `/v1/admin/users/${userId}/impersonate`,
      });
    },
  });
};
